
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import SEOMetadata from '@/components/SEOMetadata';
import { Button } from '@/components/ui/button';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Link } from 'react-router-dom';
import { ArrowRight, Zap, Globe, Shield, Users, MessageCircle } from 'lucide-react';

const WhyChooseUs = () => {
  return (
    <>
      <SEOMetadata
        title="Why Choose NEXX TELECOM | Our Story & Mission"
        description="NEXX TELECOM was built to solve real connectivity problems. From low-latency AI workloads to scalable SEO infrastructure, here's why global teams choose us."
        keywords="why choose NEXX, infrastructure solutions, AI workloads, SEO optimization, network latency, global connectivity"
        ogTitle="Why Choose NEXX TELECOM | Our Story & Mission"
        ogDescription="Discover how NEXX TELECOM evolved from solving gaming latency to providing world-class infrastructure for AI, SEO, and global connectivity needs."
        canonical="https://nexx.net/why-choose-us"
      />
      
      <div className="min-h-screen bg-white">
        <Header />
        
        {/* Hero Section */}
        <section className="pt-32 pb-16 bg-gradient-to-br from-gray-50 to-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                Why NEXX is the Smart Choice
              </h1>
              <p className="text-xl text-gray-600 leading-relaxed max-w-3xl mx-auto">
                For AI Workloads, SEO Optimization, and Real-World Infrastructure Needs
              </p>
            </div>
          </div>
        </section>

        {/* Origin Story Section */}
        <section className="py-8 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="prose prose-lg max-w-none">
              <p className="text-lg text-gray-700 leading-relaxed mb-8">
                NEXX TELECOM wasn't founded in a boardroom. It wasn't born out of a generic business plan or copied from another hosting provider. It was born out of a real problem, and that origin still drives how we think, how we build, and who we serve.
              </p>

              <div className="bg-gradient-to-r from-nexx-green/10 to-green-100/50 rounded-xl p-8 mb-12">
                <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                  <Zap className="h-6 w-6 text-nexx-green mr-3" />
                  A Story That Started With Latency
                </h2>
                <p className="text-gray-700 leading-relaxed">
                  Over 8 years ago, a group of young gamers found themselves unable to play a certain game. Not because of regional restrictions, not because of licensing issues, and certainly not because they did anything wrong. They were simply located in a part of the world where infrastructure had been neglected — the servers didn't exist nearby, and latency made the game unplayable.
                </p>
                <p className="text-gray-700 leading-relaxed mt-4">
                  At the time, there were no simple solutions. VPNs were too generic, too fragile. What we created wasn't just a tunnel — it was a dedicated network path, a kind of home-router-through-a-pipeline solution that gave each user a clean, fast, personalized route to the server infrastructure they needed. And it worked.
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                <Globe className="h-6 w-6 text-nexx-green mr-3" />
                From Gaming to Global Networking
              </h2>
              <p className="text-gray-700 leading-relaxed mb-6">
                That original problem taught us something foundational: real users, in real places, need real infrastructure. The internet isn't evenly built. It's fragmented. That means companies, creatives, and engineers across the globe are still suffering from poor connectivity, regional bias in network design, and lack of reliable access.
              </p>

              <div className="bg-gray-50 rounded-xl p-6 mb-8">
                <p className="text-gray-700 font-medium mb-4">NEXX evolved from that original solution into a globally positioned provider of:</p>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-start">
                    <ArrowRight className="h-5 w-5 text-nexx-green mr-2 mt-0.5 flex-shrink-0" />
                    Unmetered bandwidth for AI and compute-heavy applications
                  </li>
                  <li className="flex items-start">
                    <ArrowRight className="h-5 w-5 text-nexx-green mr-2 mt-0.5 flex-shrink-0" />
                    Colocation in Ashburn and other strategic locations
                  </li>
                  <li className="flex items-start">
                    <ArrowRight className="h-5 w-5 text-nexx-green mr-2 mt-0.5 flex-shrink-0" />
                    Custom network designs that route traffic where it's needed most
                  </li>
                  <li className="flex items-start">
                    <ArrowRight className="h-5 w-5 text-nexx-green mr-2 mt-0.5 flex-shrink-0" />
                    Residential proxy support infrastructure
                  </li>
                </ul>
                <p className="text-gray-700 mt-4 font-medium">
                  And we do it differently. Our stack is lean. Our hands are smart. Our turnaround times are fast.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* The Difference Section */}
        <section className="py-16 bg-gradient-to-br from-gray-50 to-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
              The Difference Is In the Thinking
            </h2>
            <p className="text-xl text-gray-700 text-center mb-12">
              Most companies sell cookie-cutter services. <span className="font-semibold text-nexx-green">NEXX solves problems.</span>
            </p>

            <div className="grid md:grid-cols-2 gap-8 mb-12">
              <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">We've seen clients come to us with:</h3>
                <ul className="space-y-3 text-gray-700">
                  <li className="flex items-start">
                    <ArrowRight className="h-4 w-4 text-nexx-green mr-2 mt-1 flex-shrink-0" />
                    High-latency connections for time-sensitive financial transactions
                  </li>
                  <li className="flex items-start">
                    <ArrowRight className="h-4 w-4 text-nexx-green mr-2 mt-1 flex-shrink-0" />
                    Censorship-limited research workflows that need global access
                  </li>
                  <li className="flex items-start">
                    <ArrowRight className="h-4 w-4 text-nexx-green mr-2 mt-1 flex-shrink-0" />
                    SEO optimization workloads that require multi-geo IP diversity
                  </li>
                  <li className="flex items-start">
                    <ArrowRight className="h-4 w-4 text-nexx-green mr-2 mt-1 flex-shrink-0" />
                    AI inference engines needing low-ping network paths and scalable colocation
                  </li>
                </ul>
              </div>
              <div className="bg-nexx-green/5 rounded-xl p-6 border border-nexx-green/20">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Our Approach:</h3>
                <p className="text-gray-700">
                  We don't just provision and walk away. We assess the challenge, build the route, and walk the edge of what's possible.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Why AI & SEO Clients Choose NEXX */}
        <section className="py-16 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">
              Why AI & SEO Clients Choose NEXX
            </h2>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
              <div className="bg-gradient-to-br from-blue-50 to-blue-100/50 rounded-xl p-6">
                <div className="w-12 h-12 bg-blue-500 rounded-lg flex items-center justify-center mb-4">
                  <Globe className="h-6 w-6 text-white" />
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">Proximity to Core Internet Exchanges</h3>
                <p className="text-gray-600 text-sm">Fast, low-latency performance</p>
              </div>
              
              <div className="bg-gradient-to-br from-green-50 to-green-100/50 rounded-xl p-6">
                <div className="w-12 h-12 bg-nexx-green rounded-lg flex items-center justify-center mb-4">
                  <Zap className="h-6 w-6 text-white" />
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">Unmetered Bandwidth Tiers</h3>
                <p className="text-gray-600 text-sm">No throttling, no surprise bills</p>
              </div>
              
              <div className="bg-gradient-to-br from-purple-50 to-purple-100/50 rounded-xl p-6">
                <div className="w-12 h-12 bg-purple-500 rounded-lg flex items-center justify-center mb-4">
                  <Shield className="h-6 w-6 text-white" />
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">Custom IP Allocation</h3>
                <p className="text-gray-600 text-sm">Ideal for SEO clusters or geographic testing</p>
              </div>
              
              <div className="bg-gradient-to-br from-orange-50 to-orange-100/50 rounded-xl p-6">
                <div className="w-12 h-12 bg-orange-500 rounded-lg flex items-center justify-center mb-4">
                  <Users className="h-6 w-6 text-white" />
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">Smart Hands Support</h3>
                <p className="text-gray-600 text-sm">Deployment assistance on-site, when it counts</p>
              </div>
              
              <div className="bg-gradient-to-br from-teal-50 to-teal-100/50 rounded-xl p-6 md:col-span-2">
                <div className="w-12 h-12 bg-teal-500 rounded-lg flex items-center justify-center mb-4">
                  <Shield className="h-6 w-6 text-white" />
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">Proxy & Automation Friendly</h3>
                <p className="text-gray-600 text-sm">Clean IPs, flexible routing, and transparent operations</p>
              </div>
            </div>

            <div className="text-center">
              <p className="text-lg text-gray-700 mb-8">
                We know the buzzwords. But we live the use cases. From gamers to global researchers, from marketers to machine learning engineers — our mission is to build infrastructure that adapts to your real-world needs.
              </p>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-16 bg-gradient-to-br from-gray-50 to-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4 flex items-center justify-center">
                <MessageCircle className="h-8 w-8 text-nexx-green mr-3" />
                Frequently Asked Questions (FAQ)
              </h2>
              <p className="text-lg text-gray-600">
                Get answers to common questions about NEXX's infrastructure solutions
              </p>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <Accordion type="single" collapsible className="w-full">
                <AccordionItem value="item-1" className="border-b border-gray-200 last:border-b-0">
                  <AccordionTrigger className="px-6 py-4 text-left hover:bg-gray-50">
                    <span className="font-semibold text-gray-900">
                      What makes NEXX different from other hosting and colocation providers?
                    </span>
                  </AccordionTrigger>
                  <AccordionContent className="px-6 pb-4 text-gray-700">
                    NEXX was built from the ground up to solve real-world connectivity issues — not just resell space. We design custom infrastructure for AI, SEO, proxy, and automation use cases. Everything we offer comes from direct problem-solving experience, not recycled plans.
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="item-2" className="border-b border-gray-200 last:border-b-0">
                  <AccordionTrigger className="px-6 py-4 text-left hover:bg-gray-50">
                    <span className="font-semibold text-gray-900">
                      Does NEXX support AI workloads and machine learning infrastructure?
                    </span>
                  </AccordionTrigger>
                  <AccordionContent className="px-6 pb-4 text-gray-700">
                    Yes. Our unmetered bandwidth, proximity to major exchanges like Ashburn, and flexible power/cooling setups make us a strong fit for AI training, inference, and data-heavy environments.
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="item-3" className="border-b border-gray-200 last:border-b-0">
                  <AccordionTrigger className="px-6 py-4 text-left hover:bg-gray-50">
                    <span className="font-semibold text-gray-900">
                      Is NEXX proxy-friendly?
                    </span>
                  </AccordionTrigger>
                  <AccordionContent className="px-6 pb-4 text-gray-700">
                    Absolutely. We provide clean U.S.-based IPs and understand the needs of clients using proxies for automation, marketing, research, and load testing. We build networks that support these use cases ethically and reliably.
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="item-4" className="border-b border-gray-200 last:border-b-0">
                  <AccordionTrigger className="px-6 py-4 text-left hover:bg-gray-50">
                    <span className="font-semibold text-gray-900">
                      Can I use NEXX for SEO hosting and testing?
                    </span>
                  </AccordionTrigger>
                  <AccordionContent className="px-6 pb-4 text-gray-700">
                    Yes. We support custom IP allocation, multi-region routing, and low-latency infrastructure for SEO testing, link building, and domain spread. If you're scaling SEO operations or doing geographic segmentation, we're ready for you.
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="item-5" className="border-b border-gray-200 last:border-b-0">
                  <AccordionTrigger className="px-6 py-4 text-left hover:bg-gray-50">
                    <span className="font-semibold text-gray-900">
                      What is Smart Hands support?
                    </span>
                  </AccordionTrigger>
                  <AccordionContent className="px-6 pb-4 text-gray-700">
                    Our Smart Hands service provides physical on-site support — including server installs, cable management, reboots, and custom requests — handled by our local team. No need to fly in or wait days to get things done.
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="item-6" className="border-b border-gray-200 last:border-b-0">
                  <AccordionTrigger className="px-6 py-4 text-left hover:bg-gray-50">
                    <span className="font-semibold text-gray-900">
                      How fast can I get deployed with NEXX?
                    </span>
                  </AccordionTrigger>
                  <AccordionContent className="px-6 pb-4 text-gray-700">
                    Most setups go live within 24–48 hours depending on your requirements. We prioritize speed and communication — because we've been on the other side waiting too long.
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section className="py-16 bg-gradient-to-br from-nexx-green to-green-600">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl font-bold text-white mb-6">
              Let's Build Something That Works
            </h2>
            <p className="text-xl text-green-100 mb-8 max-w-2xl mx-auto">
              NEXX is infrastructure for problem-solvers. If you're working in AI, SEO, app development, research, or connectivity, we're ready to design a setup that works for you — not against you.
            </p>
            <div className="space-y-4 sm:space-y-0 sm:space-x-4 sm:flex sm:justify-center">
              <Button 
                asChild
                size="lg"
                className="bg-white text-nexx-green hover:bg-gray-100 font-semibold"
              >
                <Link to="/consultation">
                  Let's Talk. Let's Solve. Let's Build.
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              </Button>
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
};

export default WhyChooseUs;
